import {
  IHouseStepPhotos,
  IWizardHouseState,
  THousePhoto,
  TStepPayload,
  TWizardStepComponentEditable,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { MultipleImagesUploader } from "../../../../MultipleImagesUploader";
import { HouseAPI } from "../../../../../API/houseApi";
import { getStepErrorsFromResponse } from "../../../utils";
import { useTranslation } from "react-i18next";

const statePath = WIZARD_HOUSE_STEPS_PATHS.Photos;
export const WizardStepPhotos: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepPhotos>>(
      state[statePath] ?? { photos: [] }
    );
    const [errors, setErrors] = useState<string | null>(null);
    const { t: terror } = useTranslation("errors");
    const save = async () => {
      setErrors(null);

      try {
        let lastUploadResult;

        lastUploadResult = await HouseAPI.wizardPhotosUploadFinish(
          state.house_id!,
          state.wizard_type!
        );

        if (lastUploadResult) {
          return {
            ...state,
            [statePath]: lastUploadResult
          };
        } else {
          return state;
        }
      } catch (e: any) {
        const errors = getStepErrorsFromResponse(e);
        if (errors) {
          setErrors(
            Object.values(errors)
              .filter((v) => v)
              .join(", ")
          );
        } else if (e?.response?.data?.message) {
          let errMsg: string;
          errMsg = e?.response?.data?.message;
          if (
            e?.response?.data?.message?.includes(
              "Two or more photos are required"
            )
          ) {
            errMsg = terror("errors.E2");
          }
          setErrors(errMsg);
        } else {
          setErrors("Something went wrong. Contact support");
        }

        throw e;
      }
    };

    useImperativeHandle(ref, () => ({
      save: save,
      getPossibleUpdatedState: () => {
        return {
          ...state,
          [statePath]: {
            ...values,
            metadata: state[statePath]?.metadata ?? {
              finished: false,
              started: true,
              updated_at: null,
              created_at: null
            }
          }
        };
      }
    }));

    return (
      <WizardStepPhotosView
        photos={values.photos ?? []}
        errors={errors}
        onImagesChanged={(images) => {
          setValues((prev) => ({
            ...prev,
            photos: images
          }));
        }}
        readonly={readonly}
        viewonly={viewonly}
        uploadPhoto={(file, order) => {
          return HouseAPI.wizardPhotosUpload(
            state.house_id!,
            state.wizard_type!,
            Date.now(),
            order,
            file,
            { showNotificationOnError: true }
          );
        }}
        deletePhoto={(fileName) => {
          return HouseAPI.wizardPhotosDelete(
            state.house_id!,
            state.wizard_type!,
            fileName
          );
        }}
        reorderPhotos={(order) => {
          return HouseAPI.wizardUpdatePhotosOrder(
            state.house_id!,
            state.wizard_type!,
            order
          );
        }}
      />
    );
  });

export const WizardStepPhotosView: React.FC<{
  photos: THousePhoto[];
  onImagesChanged: (file: THousePhoto[]) => void;
  errors: string | null;
  readonly?: boolean;
  viewonly?: boolean;
  uploadPhoto: (file: File, order: number) => Promise<THousePhoto>;
  deletePhoto: (fileName: string) => Promise<any>;
  reorderPhotos: (
    order: { file_name: string; order: number }[]
  ) => Promise<any>;
}> = React.memo(
  ({
    photos,
    onImagesChanged,
    readonly,
    viewonly,
    errors,
    uploadPhoto,
    deletePhoto,
    reorderPhotos
  }) => {
    return (
      <div>
        <MultipleImagesUploader
          images={photos}
          onImagesChanged={(newImages) => {
            onImagesChanged(newImages);
          }}
          readonly={readonly || viewonly}
          uploadImage={uploadPhoto}
          deleteImage={deletePhoto}
          reorderImages={reorderPhotos}
        />
        {errors && <div style={{ color: "red" }}>{errors}</div>}
      </div>
    );
  }
);
